// ─────────────────────────────────────────────────────────────────────────────
// StatsRow.js — the four KPI tiles from /enterprise-nodes/summary (+ the node list).
//
// Every value and every sub-line here has to trace back to a real reading.
// What used to be here, and why it's gone:
//
//   • "N nodes healthy" counted ONLINE nodes. Online ≠ healthy — a node can be
//     online and Critical. It now counts nodes actually reported as Optimal.
//   • "Nominal load" was printed whenever avg_network_load_pct was missing,
//     because `undefined > 70` is false. A missing reading now prints nothing.
//   • "Optimal capacity" was hardcoded under the bandwidth figure — a verdict on
//     capacity we have no data for. Replaced by a description of the number.
//   • Every tile passed an up-and-right trend arrow. An arrow claims a direction
//     of travel and the API returns no deltas, so no tile carries one until it
//     does. (StatCard still supports `trendIcon` for when they exist.)
//
// If the summary call fails the tiles show "—" instead of something plausible;
// the banner above the table explains why.
// ─────────────────────────────────────────────────────────────────────────────
import styles from "./StatsRow.module.css";
import StatCard from "./StatCard";
import { Icons } from "../../../components/Icons";
import { DASH, fmtCount, fmtPercent } from "../format";

export default function StatsRow({ summary, nodes, loading }) {
  // The summary is authoritative; the list is the fallback when only that call
  // failed, since counting rows we already hold is still a real reading.
  const online  = summary?.active_nodes?.value ?? nodes.filter((n) => n.online).length;
  const total   = summary?.active_nodes?.total ?? nodes.length;
  const optimal = nodes.filter((n) => n.status === "Optimal").length;

  const load      = summary?.avg_network_load_pct;
  const bandwidth = summary?.total_bandwidth_gbps;
  const hasBandwidth = Number.isFinite(bandwidth);

  // Only describe the load when there is a load to describe.
  const loadSub = Number.isFinite(load)
    ? (load > 70 ? "High traffic period" : "Nominal load")
    : "";

  return (
    <div className={styles.row}>
      <StatCard
        highlight
        accent="#00e87a"
        icon={Icons.server}
        label="Active Nodes"
        value={loading ? DASH : `${online}/${total}`}
        // Real count of Optimal nodes — not a restatement of the online count.
        sub={loading || nodes.length === 0 ? "" : `${optimal} optimal`}
        delay={0}
      />
      <StatCard
        accent="#3b82f6"
        icon={Icons.users}
        label="Total Users"
        value={loading ? DASH : fmtCount(summary?.total_users)}
        sub={loading || !summary ? "" : `${fmtCount(summary.enterprise_users)} enterprise`}
        delay={80}
      />
      <StatCard
        accent="#ffc542"
        icon={Icons.activity}
        label="Avg Network Load"
        value={loading ? DASH : fmtPercent(load, 1)}
        sub={loading ? "" : loadSub}
        delay={160}
      />
      <StatCard
        accent="#00c8e8"
        icon={Icons.wifi}
        label="Total Bandwidth"
        value={loading || !hasBandwidth ? DASH : bandwidth.toFixed(2)}
        unit={loading || !hasBandwidth ? "" : "Gbps"}
        // A description of the figure, not a verdict on it.
        sub={loading || !hasBandwidth ? "" : "Live throughput"}
        delay={240}
      />
    </div>
  );
}
